import type { ReactElement } from 'react'
import type { ImageDTO } from '@shared/types'
import { Skeleton } from '@/components/ui'
import { cn } from '@/lib/cn'
import { ImageCard } from './ImageCard'

export interface ImageGridProps {
  items: ImageDTO[]
  /** Already trimmed of a trailing slash. */
  siteUrl: string
  loading: boolean
  /** A job owns this site right now. */
  siteBusy: boolean
  /** That job is a SAVE, so approved-and-unsaved rows are mid-write. */
  savingActive: boolean
}

const COLUMNS = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'

/** Enough to fill the widest breakpoint's first two rows. */
const PLACEHOLDER_IDS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8']

function CardSkeleton(): ReactElement {
  return (
    <div className="card flex flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="size-[1.125rem]" />
      </div>
      <Skeleton className="aspect-[4/3] w-full" />
      <div className="space-y-2 px-3 py-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-18 w-full" />
        <div className="flex gap-2 pt-1">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-28" />
        </div>
      </div>
    </div>
  )
}

export function ImageGrid({
  items,
  siteUrl,
  loading,
  siteBusy,
  savingActive
}: ImageGridProps): ReactElement {
  // A first load gets skeletons in the real card shape; a reload (page or filter
  // change) keeps the current cards and just dims them, so the page never jumps.
  if (loading && items.length === 0) {
    return (
      <div className={COLUMNS} aria-busy="true">
        <span className="sr-only">Loading images…</span>
        {PLACEHOLDER_IDS.map((id) => (
          <CardSkeleton key={id} />
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn(COLUMNS, loading && 'pointer-events-none opacity-60 transition-opacity')}
      aria-busy={loading || undefined}
    >
      {items.map((image) => (
        <ImageCard
          key={image.id}
          image={image}
          siteUrl={siteUrl}
          siteBusy={siteBusy}
          saving={savingActive && image.approved && !image.saved}
        />
      ))}
    </div>
  )
}
